import { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import {
  Mail,
  Clock,
  Eye,
  Search,
  Inbox,
  Pencil,
  Plus,
  Settings2,
  PanelLeftClose,
  PanelLeft,
  CheckCircle,
} from 'lucide-react';
import { EmailScheduledTab } from './tabs/EmailScheduledTab';
import { EmailTemplatesTab } from './tabs/EmailTemplatesTab';
import { ManageVariablesPopover } from './components/ManageVariablesPopover';
import { TemplateEditorModal } from './components/TemplateEditorModal';
import { useAuth } from '@core/auth/useAuth';
import { emailService } from '@core/supabase/services/emailService';
import { optimizationService } from '@core/supabase/services/optimizationService';
import { EmailThread } from '@core/types';
import { EmptyState } from '@shared/components/ui/EmptyState';
import { TablePagination } from '@shared/components/ui/TablePagination';
import { listExportNotImplemented } from '@shared/lib/listExportStub';
import { downloadEmailThreadsCsv } from './emailThreadsListCsv';
import { GenericManageRightsModal } from '@shared/components/list/GenericManageRightsModal';
import { formatCreatedWhen } from '@shared/lib/formatCreatedWhen';
import { stripHtml } from '@core/utils/htmlPlain';
import { emailDeliveryKind, emailDeliveryLabel, emailDeliveryDotColor } from './emailDeliveryUi';
import { StatusChip } from '@shared/components/ui/StatusChip';
import { EmailThreadPreviewPanel } from './EmailThreadPreviewPanel';
import { EmailComposeView } from './EmailComposePage';
import { emailTemplateService, type EmailTemplate } from '@core/supabase/services/emailTemplateService';

type FilterTab = 'all' | 'pending' | 'responded' | 'no_reply';

type EmailSortCol = 'client' | 'candidate' | 'job' | 'subject' | 'response' | 'opens' | 'sent' | 'created';

type AppRow = {
  client?: { name: string };
  candidate?: { full_name: string };
  job?: { title: string };
} | undefined;

function InboxSkeleton() {
  return (
    <div className="card overflow-hidden p-0 grid lg:grid-cols-[minmax(280px,380px)_1fr] min-h-[420px]" aria-busy="true" aria-label="Loading inbox">
      <div className="border-b lg:border-b-0 lg:border-r border-[var(--vscode-border)] p-3 space-y-2">
        {Array.from({ length: 6 }).map((_, i) => (
          <div key={i} className="h-16 rounded-lg animate-pulse vscode-bg-tertiary/80" />
        ))}
      </div>
      <div className="hidden lg:block p-6 space-y-3">
        <div className="h-6 w-1/2 max-w-md animate-pulse rounded vscode-bg-tertiary" />
        <div className="h-4 w-3/4 animate-pulse rounded vscode-bg-tertiary" />
        <div className="h-40 animate-pulse rounded-lg vscode-bg-tertiary mt-4" />
      </div>
    </div>
  );
}

function InboxListRow({
  thread,
  selected,
  active,
  onToggleSelect,
  onOpen,
}: {
  thread: EmailThread;
  selected: boolean;
  active: boolean;
  onToggleSelect: () => void;
  onOpen: () => void;
}) {
  const isResponded = !!thread.cta_response;
  const isOverdue = !isResponded && thread.next_followup_at && new Date(thread.next_followup_at) < new Date();
  const app = thread.application as AppRow;
  const clientName = app?.client?.name ?? thread.recipient_name ?? '—';
  const candidateName = app?.candidate?.full_name?.trim() || '—';
  const kind = emailDeliveryKind(thread);
  const snippet = stripHtml(thread.body_html || '')
    .replace(/\s+/g, ' ')
    .trim()
    .slice(0, 90);

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={onOpen}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onOpen();
        }
      }}
      className={[
        'flex gap-2 px-3 py-2.5 border-b border-[var(--vscode-border)]/55 cursor-pointer text-left transition-colors',
        active ? 'bg-[color-mix(in_srgb,var(--vscode-text-link)_10%,transparent)]' : 'hover:vscode-bg-hover',
        selected ? 'ring-1 ring-inset ring-[var(--vscode-text-link)]/35' : '',
      ].join(' ')}
    >
      <div className="pt-0.5 shrink-0" onClick={(e) => e.stopPropagation()}>
        <input type="checkbox" checked={selected} onChange={onToggleSelect} className="rounded border-gray-600" />
      </div>
      <div className="min-w-0 flex-1 space-y-1">
        <div className="flex flex-wrap items-center gap-1.5 gap-y-1">
          <StatusChip label={emailDeliveryLabel(kind)} dotColor={emailDeliveryDotColor(kind)} />
          <span className="text-[10.5px] tabular-nums vscode-text-tertiary">
            {formatCreatedWhen(thread.created_at)}
          </span>
        </div>
        <p className="line-clamp-2 text-[12.5px] font-semibold leading-snug vscode-text-primary">
          {thread.subject?.trim() || '—'}
        </p>
        <p className="truncate text-[11px] vscode-text-secondary">
          {clientName} · {candidateName}
        </p>
        {snippet ? (
          <p className="line-clamp-2 text-[11px] leading-snug vscode-text-tertiary">{snippet}</p>
        ) : null}
        <div className="flex flex-wrap items-center gap-2 pt-0.5">
          {!isResponded && (
            <span
              className={`inline-flex items-center gap-1 text-[10.5px] ${
                isOverdue ? 'text-amber-500' : 'vscode-text-tertiary'
              }`}
            >
              <Clock className="h-3 w-3 shrink-0" />
              {isOverdue ? 'Follow-up due' : 'Awaiting reply'}
            </span>
          )}
          <span className="inline-flex items-center gap-1 text-[10.5px] vscode-text-tertiary">
            <Eye className="h-3 w-3 shrink-0" />
            {thread.open_count > 0 ? `${thread.open_count} opens` : 'No opens'}
          </span>
        </div>
      </div>
    </div>
  );
}

function pickDesktopThread(): boolean {
  if (typeof window === 'undefined') return false;
  return window.matchMedia('(min-width: 1024px)').matches;
}

export function EmailPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const sentId = searchParams.get('sent');
  const threadParam = searchParams.get('thread');

  // ── Top-level tabs: Sent / Scheduled / Templates (§11-A) ──
  type TopTab = 'sent' | 'scheduled' | 'templates';
  const rawTopTab = searchParams.get('etab');
  const topTab: TopTab =
    rawTopTab === 'scheduled' || rawTopTab === 'templates' ? rawTopTab : 'sent';
  const setTopTab = (t: TopTab) => {
    // Allow the auto-select effect to pick a thread again on Sent after
    // the URL's `thread` param is cleared during the tab switch.
    didInitThreadRef.current = false;
    setSearchParams(
      (prev) => {
        const n = new URLSearchParams(prev);
        if (t === 'sent') n.delete('etab'); else n.set('etab', t);
        n.delete('thread');
        return n;
      },
      { replace: true },
    );
  };

  const [threads, setThreads] = useState<EmailThread[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState<FilterTab>('all');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [manageRightsOpen, setManageRightsOpen] = useState(false);
  const [pageToast, setPageToast] = useState<{ kind: 'ok' | 'err'; text: string } | null>(null);
  const headerCheckRef = useRef<HTMLInputElement>(null);
  const [emailSortBy, setEmailSortBy] = useState<EmailSortCol>('created');
  const [emailSortAsc, setEmailSortAsc] = useState(false);
  const didInitThreadRef = useRef(false);
  const [leftPanelCollapsed, setLeftPanelCollapsed] = useState(false);
  const [composingNew, setComposingNew] = useState(false);
  /** Right-panel reply composer (same column as "New Email", keeps thread preview list + reading pane context). */
  const [inlineReplyToThreadId, setInlineReplyToThreadId] = useState<string | null>(null);
  /** Pre-selects a template when opening the inline compose (e.g. "Use in Email"). */
  const [composeInitialTemplate, setComposeInitialTemplate] = useState<string | undefined>();
  /** Org templates — loaded here and passed to the inline compose so it never shows stale data. */
  const [orgTemplates, setOrgTemplates] = useState<EmailTemplate[]>([]);

  const refreshOrgTemplates = useCallback(() => {
    if (!user?.orgId) return;
    emailTemplateService.list(user.orgId).then(setOrgTemplates).catch(() => {});
  }, [user?.orgId]);

  useEffect(() => { refreshOrgTemplates(); }, [refreshOrgTemplates]);

  // ── Templates-tab toolbar state (lifted so buttons live in the tab strip) ──
  const [templateVarsOpen, setTemplateVarsOpen] = useState(false);
  const [templateEditorOpen, setTemplateEditorOpen] = useState(false);
  const [templateRefreshKey, setTemplateRefreshKey] = useState(0);
  const varsBtnRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setPage(1);
  }, [filter, search]);

  const pushToast = useCallback((kind: 'ok' | 'err', text: string) => {
    setPageToast({ kind, text });
  }, []);

  useEffect(() => {
    if (!pageToast) return;
    const id = window.setTimeout(() => setPageToast(null), 4200);
    return () => window.clearTimeout(id);
  }, [pageToast]);

  useEffect(() => {
    if (!user?.orgId) return;
    emailService.listByOrg(user.orgId).then(setThreads).finally(() => setLoading(false));
  }, [user?.orgId]);

  const handleStopFollowup = async (id: string) => {
    await emailService.stopFollowups(id);
    setThreads((prev) => prev.map((t) => (t.id === id ? { ...t, followup_stopped: true, next_followup_at: null } : t)));
  };

  const handleRetrigger = async (thread: EmailThread) => {
    if (!thread.body_html) return;
    try {
      const opt = await optimizationService.getByApplication(thread.application_id);
      const res = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/ggj_b2b_send-email`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`,
        },
        body: JSON.stringify({
          thread_id: thread.id,
          to: thread.recipient_email,
          subject: thread.subject,
          body_html: thread.body_html,
          ...(opt?.masked_resume_url && { masked_resume_path: opt.masked_resume_url }),
        }),
      });
      const result = await res.json();
      if (!res.ok || result.error) throw new Error(result.error ?? 'Failed to resend');
      setThreads((prev) =>
        prev.map((t) => (t.id === thread.id ? { ...t, sent_at: new Date().toISOString() } : t)),
      );
      pushToast('ok', 'Email resent.');
    } catch (err) {
      console.error('Retrigger failed:', err);
      pushToast('err', err instanceof Error ? err.message : 'Failed to resend email');
    }
  };

  const openThread = useCallback(
    (thread: EmailThread) => {
      if (pickDesktopThread()) {
        setSearchParams(
          (prev) => {
            const n = new URLSearchParams(prev);
            n.set('thread', thread.id);
            return n;
          },
          { replace: true },
        );
      } else {
        navigate(`/emails/thread/${thread.id}`);
      }
    },
    [navigate, setSearchParams],
  );

  const filteredThreads = useMemo(() => {
    return threads.filter((t) => {
      if (filter === 'pending' && (!!t.cta_response || !!t.followup_stopped)) return false;
      if (filter === 'responded' && !t.cta_response) return false;
      if (filter === 'no_reply' && (!!t.cta_response || !t.followup_stopped)) return false;
      if (search.trim()) {
        const q = search.toLowerCase();
        const app = t.application as AppRow;
        const haystack = [
          app?.client?.name,
          app?.candidate?.full_name,
          app?.job?.title,
          t.recipient_name,
          t.recipient_email,
          t.subject,
          emailDeliveryLabel(emailDeliveryKind(t)),
        ]
          .filter(Boolean)
          .join(' ')
          .toLowerCase();
        if (!haystack.includes(q)) return false;
      }
      return true;
    });
  }, [threads, filter, search]);

  const sortedThreads = useMemo(() => {
    const arr = [...filteredThreads];
    const dir = emailSortAsc ? 1 : -1;
    const str = (a: EmailThread, b: EmailThread, g: (t: EmailThread) => string) =>
      dir * g(a).localeCompare(g(b), undefined, { sensitivity: 'base' });
    const num = (a: EmailThread, b: EmailThread, g: (t: EmailThread) => number) => dir * (g(a) - g(b));
    arr.sort((a, b) => {
      switch (emailSortBy) {
        case 'client': {
          const ga = (t: EmailThread) => {
            const ap = t.application as { client?: { name: string } } | undefined;
            return ap?.client?.name ?? t.recipient_name ?? '';
          };
          return str(a, b, ga);
        }
        case 'candidate': {
          const gc = (t: EmailThread) => {
            const ap = t.application as { candidate?: { full_name: string } } | undefined;
            return ap?.candidate?.full_name ?? '';
          };
          return str(a, b, gc);
        }
        case 'job': {
          const gj = (t: EmailThread) => {
            const ap = t.application as { job?: { title: string } } | undefined;
            return ap?.job?.title ?? '';
          };
          return str(a, b, gj);
        }
        case 'subject':
          return str(a, b, (t) => t.subject ?? '');
        case 'response':
          return str(a, b, (t) => (t.cta_response ? String(t.cta_response) : ''));
        case 'opens':
          return num(a, b, (t) => t.open_count);
        case 'sent':
          return (
            dir *
            ((a.sent_at ? new Date(a.sent_at).getTime() : 0) - (b.sent_at ? new Date(b.sent_at).getTime() : 0))
          );
        case 'created':
        default:
          return dir * (new Date(a.created_at).getTime() - new Date(b.created_at).getTime());
      }
    });
    return arr;
  }, [filteredThreads, emailSortBy, emailSortAsc]);

  /** Drop invalid `thread` query param only if the id is not in the org list at all.
   *  (Use `threads`, not `sortedThreads` — a thread can be valid but hidden by the
   *  filter/search, and we still want the preview panel to work after send.) */
  useEffect(() => {
    const id = threadParam;
    if (!id) return;
    if (threads.some((t) => t.id === id)) return;
    setSearchParams(
      (prev) => {
        const n = new URLSearchParams(prev);
        n.delete('thread');
        return n;
      },
      { replace: true },
    );
  }, [threads, threadParam, setSearchParams]);

  const totalItems = sortedThreads.length;
  const paginated = sortedThreads.slice((page - 1) * pageSize, page * pageSize);

  useEffect(() => {
    if (!pickDesktopThread()) return;
    if (threadParam) {
      didInitThreadRef.current = true;
      return;
    }
    if (paginated.length === 0) return;
    if (didInitThreadRef.current) return;
    setSearchParams(
      (prev) => {
        const n = new URLSearchParams(prev);
        n.set('thread', paginated[0].id);
        return n;
      },
      { replace: true },
    );
    didInitThreadRef.current = true;
  }, [paginated, threadParam, setSearchParams]);

  useEffect(() => {
    if (headerCheckRef.current) {
      headerCheckRef.current.indeterminate =
        paginated.length > 0 && selectedIds.size > 0 && selectedIds.size < paginated.length;
    }
  }, [paginated.length, selectedIds.size]);

  useEffect(() => {
    if (!inlineReplyToThreadId) return;
    if (threadParam === inlineReplyToThreadId) return;
    setInlineReplyToThreadId(null);
  }, [threadParam, inlineReplyToThreadId]);

  const selectedThread = threadParam
    ? sortedThreads.find((t) => t.id === threadParam) ?? threads.find((t) => t.id === threadParam) ?? null
    : null;

  const runEmailThreadsExport = useCallback(
    (fmt: ListExportFmt, scope: 'selection' | 'all') => {
      if (fmt !== 'csv') {
        listExportNotImplemented('Email activity', fmt, scope);
        return;
      }
      let rows = sortedThreads;
      if (scope === 'selection') {
        if (selectedIds.size === 0) {
          pushToast('err', 'Select rows to export, or use CSV — all under Operations.');
          return;
        }
        rows = sortedThreads.filter((t) => selectedIds.has(t.id));
      }
      if (rows.length === 0) {
        pushToast('err', 'Nothing to export for the current filters.');
        return;
      }
      downloadEmailThreadsCsv(rows);
    },
    [sortedThreads, selectedIds, pushToast],
  );

  const counts = {
    all: threads.length,
    pending: threads.filter((t) => !t.cta_response && !t.followup_stopped).length,
    responded: threads.filter((t) => !!t.cta_response).length,
    no_reply: threads.filter((t) => !t.cta_response && t.followup_stopped).length,
  };

  const FILTER_TABS: { key: FilterTab; label: string; count: number }[] = [
    { key: 'all', label: 'All', count: counts.all },
    { key: 'pending', label: 'Pending', count: counts.pending },
    { key: 'responded', label: 'Responded', count: counts.responded },
    { key: 'no_reply', label: 'No Reply', count: counts.no_reply },
  ];

  const sortLabels: { id: EmailSortCol; label: string }[] = [
    { id: 'created', label: 'Created' },
    { id: 'sent', label: 'Sent' },
    { id: 'client', label: 'Client' },
    { id: 'candidate', label: 'Candidate' },
    { id: 'job', label: 'Job' },
    { id: 'subject', label: 'Subject' },
    { id: 'response', label: 'Response' },
    { id: 'opens', label: 'Opens' },
  ];

  // ── Tab strip definition ──
  const TOP_TABS: { key: TopTab; label: string }[] = [
    { key: 'sent', label: 'Sent' },
    { key: 'scheduled', label: 'Scheduled' },
    { key: 'templates', label: 'Templates' },
  ];

  return (
    <div className="w-full pb-8">
      {/* ── Top tabs: Sent / Scheduled / Templates + contextual right actions ── */}
      <div
        className="relative flex items-center justify-between border-b border-[var(--vscode-border-primary)] mb-4 px-0"
        style={{ height: 44 }}
      >
        {/* Tab buttons */}
        <div className="flex h-full items-center" role="tablist" aria-label="Email sections">
          {TOP_TABS.map((t) => {
            const active = topTab === t.key;
            return (
              <button
                key={t.key}
                type="button"
                role="tab"
                aria-selected={active}
                onClick={() => setTopTab(t.key)}
                className={[
                  'relative flex h-full items-center px-4 transition-colors',
                  active
                    ? 'text-[13px] font-medium vscode-text-primary'
                    : 'text-[11px] font-normal vscode-text-tertiary uppercase tracking-wide hover:vscode-text-secondary',
                ].join(' ')}
              >
                {t.label}
                {active && (
                  <span className="absolute bottom-0 left-0 right-0 h-0.5 rounded-full bg-emerald-500" />
                )}
              </button>
            );
          })}
        </div>

        {/* Right-side actions — shown on all tabs */}
        {user?.orgId && (
          <div className="flex shrink-0 items-center gap-2 pr-1">
            <div ref={varsBtnRef} className="relative">
              <button
                type="button"
                onClick={() => setTemplateVarsOpen((o) => !o)}
                aria-expanded={templateVarsOpen}
                className="inline-flex h-8 items-center gap-1.5 rounded-[8px] border px-2.5 text-[12px] font-medium transition-colors hover:bg-[var(--vscode-bg-hover)]"
                style={{
                  background: 'var(--vscode-bg-elevated)',
                  borderColor: 'var(--vscode-border-primary)',
                  color: 'var(--vscode-text-primary)',
                }}
              >
                <Settings2 className="h-3.5 w-3.5" aria-hidden />
                Manage Variables
              </button>
              {templateVarsOpen && (
                <ManageVariablesPopover
                  orgId={user.orgId}
                  onClose={() => setTemplateVarsOpen(false)}
                />
              )}
            </div>

            <button
              type="button"
              onClick={() => setTemplateEditorOpen(true)}
              className="inline-flex h-8 items-center gap-1.5 rounded-[8px] px-3 text-[12px] font-medium text-white transition-opacity hover:opacity-90"
              style={{ background: '#10B981' }}
            >
              <Plus className="h-3.5 w-3.5" />
              Create template
            </button>
          </div>
        )}
      </div>

      {/* ── Scheduled tab ── */}
      {topTab === 'scheduled' && (
        <EmailScheduledTab
          threads={threads}
          loading={loading}
          onThreadUpdated={(next) =>
            setThreads((prev) => prev.map((t) => (t.id === next.id ? next : t)))
          }
        />
      )}

      {/* ── Templates tab ── */}
      {topTab === 'templates' && (
        <>
          <EmailTemplatesTab
            refreshKey={templateRefreshKey}
            editorOpen={templateEditorOpen}
            onEditorClose={() => setTemplateEditorOpen(false)}
            onSaved={() => {
              setTemplateRefreshKey((k) => k + 1);
              refreshOrgTemplates();
            }}
            onUseInEmail={(templateSelectValue) => {
              // Navigate to Sent tab, open inline compose with this template pre-selected
              setTopTab('sent');
              setComposeInitialTemplate(templateSelectValue);
              setComposingNew(true);
            }}
          />
          {/* Manage Variables popover and Template editor modal are rendered above (in tab strip) */}
        </>
      )}

      {/* ── Sent tab (existing inbox split) ── */}
      {topTab === 'sent' && (
      <>

      {sentId && (
        <div className="mb-4 flex items-center gap-2 rounded-lg border border-[var(--vscode-border)] bg-[color-mix(in_srgb,var(--vscode-bg-secondary)_88%,transparent)] p-3 text-sm vscode-text-primary">
          <CheckCircle className="h-4 w-4 shrink-0 opacity-80 vscode-text-secondary" aria-hidden />
          Email sent successfully. Follow-up scheduler is active.
        </div>
      )}

      {loading ? (
        <InboxSkeleton />
      ) : filteredThreads.length === 0 ? (
        <EmptyState
          icon={Mail}
          title={search ? 'No results found' : 'No emails yet'}
          description={
            search
              ? `No threads match "${search}".`
              : "Send your first email from an application's detail page."
          }
        />
      ) : (
        /* Full-page Outlook-style two-column layout */
        <div
          className={[
            'grid h-[calc(100vh-10rem)] gap-3 p-2',
            leftPanelCollapsed
              ? 'lg:grid-cols-[48px_minmax(0,1fr)]'
              : 'lg:grid-cols-[minmax(320px,400px)_minmax(0,1fr)]',
          ].join(' ')}
        >
          {/* ── LEFT PANEL — boxed with visible border ── */}
          <div
            className={[
              'flex min-h-0 min-w-0 flex-col overflow-hidden rounded-lg border transition-all duration-200',
              leftPanelCollapsed ? 'items-center' : '',
            ].join(' ')}
            style={{ borderColor: '#6B7280' }}
          >
            {/* Collapse toggle */}
            <div
              className="flex shrink-0 items-center border-b px-2 py-1.5"
              style={{ borderColor: '#6B7280', background: 'var(--vscode-bg-secondary)' }}
            >
              <button
                type="button"
                onClick={() => setLeftPanelCollapsed((c) => !c)}
                className="inline-flex h-7 w-7 items-center justify-center rounded-md transition-colors hover:bg-[var(--vscode-bg-hover)]"
                title={leftPanelCollapsed ? 'Expand panel' : 'Collapse panel'}
                style={{ color: 'var(--vscode-text-secondary)' }}
              >
                {leftPanelCollapsed ? (
                  <PanelLeft className="h-4 w-4" />
                ) : (
                  <PanelLeftClose className="h-4 w-4" />
                )}
              </button>
              {!leftPanelCollapsed && (
                <span className="ml-2 text-[11px] font-medium vscode-text-secondary">Inbox</span>
              )}
            </div>

            {!leftPanelCollapsed && (
              <>
            {/* Left panel toolbar */}
            <div
              className="shrink-0 border-b"
              style={{
                borderColor: '#6B7280',
                background: 'var(--vscode-bg-secondary)',
              }}
            >
              {/* Row 1: Search + New Email */}
              <div className="flex items-center gap-2 px-3 py-2">
              <div className="relative min-w-0 flex-1">
                <Search
                  className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 vscode-text-tertiary"
                  aria-hidden
                />
                <input
                  className="h-7 w-full rounded-[6px] border bg-transparent pl-7 pr-2 text-[12px] outline-none placeholder:vscode-text-tertiary"
                  placeholder="Search emails"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  style={{
                    borderColor: 'var(--vscode-border-primary)',
                    color: 'var(--vscode-text-primary)',
                  }}
                />
              </div>
              <button
                type="button"
                onClick={() => {
                  setInlineReplyToThreadId(null);
                  setComposingNew(true);
                  // Clear any selected thread so the right panel swaps to compose cleanly
                  if (threadParam) {
                    const next = new URLSearchParams(searchParams);
                    next.delete('thread');
                    setSearchParams(next, { replace: true });
                  }
                }}
                className="inline-flex h-7 shrink-0 items-center gap-1.5 rounded-[6px] px-2.5 text-[11px] font-medium text-white"
                style={{ background: '#10B981' }}
              >
                <Plus className="h-3.5 w-3.5" />
                New Email
              </button>
            </div>
              </div>
              {/* Row 2: Count + Filter + Sort */}
              <div
                className="flex items-center gap-1.5 border-t px-3 py-1.5"
                style={{ borderColor: '#6B7280' }}
              >
              <Inbox className="h-3.5 w-3.5 shrink-0 vscode-text-tertiary" aria-hidden />
              <span className="text-[10.5px] tabular-nums vscode-text-tertiary">
                {totalItems}
              </span>
              <div className="ml-auto flex items-center gap-1.5">
                <select
                  className="h-6 rounded-[5px] border bg-transparent px-1.5 text-[10.5px] outline-none"
                  value={filter}
                  onChange={(e) => setFilter(e.target.value as FilterTab)}
                  style={{
                    borderColor: 'var(--vscode-border-primary)',
                    color: 'var(--vscode-text-secondary)',
                  }}
                >
                  {FILTER_TABS.map(({ key, label, count }) => (
                    <option key={key} value={key}>
                      {label}
                      {count > 0 ? ` (${count})` : ''}
                    </option>
                  ))}
                </select>
                <select
                  className="h-6 rounded-[5px] border bg-transparent px-1.5 text-[10.5px] outline-none"
                  value={emailSortBy}
                  onChange={(e) => {
                    const col = e.target.value as EmailSortCol;
                    setEmailSortBy(col);
                    const numericDesc = ['opens', 'sent', 'created'].includes(col);
                    setEmailSortAsc(col === 'created' ? false : numericDesc ? false : true);
                    setPage(1);
                  }}
                  style={{
                    borderColor: 'var(--vscode-border-primary)',
                    color: 'var(--vscode-text-secondary)',
                  }}
                >
                  {sortLabels.map((o) => (
                    <option key={o.id} value={o.id}>
                      Sort: {o.label}
                    </option>
                  ))}
                </select>
                <button
                  type="button"
                  className="h-6 rounded-[5px] border px-1.5 text-[10.5px] transition-colors hover:bg-[var(--vscode-bg-hover)]"
                  onClick={() => {
                    setEmailSortAsc((x) => !x);
                    setPage(1);
                  }}
                  title="Toggle sort direction"
                  style={{
                    borderColor: 'var(--vscode-border-primary)',
                    color: 'var(--vscode-text-secondary)',
                  }}
                >
                  {emailSortAsc ? 'Asc' : 'Desc'}
                </button>
              </div>
            </div>
            {/* Scrollable email list */}
            <div
              className="min-h-0 flex-1 overflow-y-auto border-t"
              style={{ borderColor: '#6B7280' }}
            >
              {paginated.map((thread) => (
                <InboxListRow
                  key={thread.id}
                  thread={thread}
                  selected={selectedIds.has(thread.id)}
                  active={threadParam === thread.id}
                  onToggleSelect={() => {
                    setSelectedIds((prev) => {
                      const next = new Set(prev);
                      if (next.has(thread.id)) next.delete(thread.id);
                      else next.add(thread.id);
                      return next;
                    });
                  }}
                  onOpen={() => openThread(thread)}
                />
              ))}
            </div>
            {/* Pagination */}
            <div
              className="shrink-0 border-t"
              style={{ borderColor: '#6B7280' }}
            >
              <TablePagination
                page={page}
                pageSize={pageSize}
                totalItems={totalItems}
                onPageChange={setPage}
                onPageSizeChange={(size) => {
                  setPageSize(size);
                  setPage(1);
                }}
              />
            </div>
              </>
            )}
          </div>

          {/* ── RIGHT PANEL — boxed with visible border ── */}
          <div
            className="hidden min-h-0 min-w-0 overflow-hidden rounded-lg border lg:flex lg:flex-col"
            style={{ borderColor: '#6B7280' }}
          >
            {composingNew ? (
              <EmailComposeView
                applicationId={null}
                embedded
                orgTemplates={orgTemplates}
                initialTemplateSelect={composeInitialTemplate}
                onCancel={() => {
                  setComposingNew(false);
                  setComposeInitialTemplate(undefined);
                }}
                onSent={async (threadId) => {
                  setComposeInitialTemplate(undefined);
                  setPageToast({ kind: 'ok', text: 'Email sent — preview below.' });
                  if (!threadId) {
                    setComposingNew(false);
                    return;
                  }
                  try {
                    if (user?.orgId) {
                      const row = await emailService.getById(threadId);
                      if (row) {
                        setThreads((prev) => {
                          if (prev.some((t) => t.id === row.id)) {
                            return prev.map((t) => (t.id === row.id ? row : t));
                          }
                          return [row, ...prev];
                        });
                      } else {
                        const list = await emailService.listByOrg(user.orgId);
                        setThreads(list);
                      }
                    }
                    // Make sure the new thread is visible in the list (not hidden by filter/search)
                    // and the preview can resolve in the right panel.
                    setFilter('all');
                    setSearch('');
                    setPage(1);
                    setSearchParams(
                      (prev) => {
                        const n = new URLSearchParams(prev);
                        n.set('thread', threadId);
                        return n;
                      },
                      { replace: true },
                    );
                    didInitThreadRef.current = true;
                  } catch {
                    if (user?.orgId) {
                      const list = await emailService.listByOrg(user.orgId);
                      setThreads(list);
                    }
                    setFilter('all');
                    setSearch('');
                    setPage(1);
                    setSearchParams(
                      (prev) => {
                        const n = new URLSearchParams(prev);
                        n.set('thread', threadId);
                        return n;
                      },
                      { replace: true },
                    );
                    didInitThreadRef.current = true;
                  } finally {
                    setComposingNew(false);
                  }
                }}
              />
            ) : inlineReplyToThreadId && selectedThread && selectedThread.id === inlineReplyToThreadId ? (
              <EmailComposeView
                key={inlineReplyToThreadId}
                applicationId={selectedThread.application_id}
                replyToThreadId={inlineReplyToThreadId}
                replyToThread={selectedThread}
                embedded
                orgTemplates={orgTemplates}
                onCancel={() => setInlineReplyToThreadId(null)}
                onSent={async (newThreadId) => {
                  setPageToast({ kind: 'ok', text: 'Reply sent — preview below.' });
                  if (!newThreadId) {
                    setInlineReplyToThreadId(null);
                    return;
                  }
                  try {
                    if (user?.orgId) {
                      const row = await emailService.getById(newThreadId);
                      if (row) {
                        setThreads((prev) => {
                          if (prev.some((t) => t.id === row.id)) {
                            return prev.map((t) => (t.id === row.id ? row : t));
                          }
                          return [row, ...prev];
                        });
                      } else {
                        const list = await emailService.listByOrg(user.orgId);
                        setThreads(list);
                      }
                    }
                    setFilter('all');
                    setSearch('');
                    setPage(1);
                    setSearchParams(
                      (prev) => {
                        const n = new URLSearchParams(prev);
                        n.set('thread', newThreadId);
                        return n;
                      },
                      { replace: true },
                    );
                    didInitThreadRef.current = true;
                  } catch {
                    if (user?.orgId) {
                      const list = await emailService.listByOrg(user.orgId);
                      setThreads(list);
                    }
                    setFilter('all');
                    setSearch('');
                    setPage(1);
                    setSearchParams(
                      (prev) => {
                        const n = new URLSearchParams(prev);
                        n.set('thread', newThreadId);
                        return n;
                      },
                      { replace: true },
                    );
                    didInitThreadRef.current = true;
                  } finally {
                    setInlineReplyToThreadId(null);
                  }
                }}
              />
            ) : selectedThread ? (
              <EmailThreadPreviewPanel
                thread={selectedThread}
                onStopFollowup={handleStopFollowup}
                onRetrigger={handleRetrigger}
                onInlineReply={() => setInlineReplyToThreadId(selectedThread.id)}
              />
            ) : (
              <div className="flex h-full flex-col items-center justify-center p-8 text-center">
                <Mail className="mb-3 h-10 w-10 opacity-60 vscode-text-tertiary" aria-hidden />
                <p className="text-sm font-medium vscode-text-primary">Select a thread</p>
                <p className="mt-1 max-w-xs text-xs vscode-text-secondary">
                  Preview the message body and jump to reply or the full thread page, or click <span className="font-medium">New Email</span> to start composing.
                </p>
              </div>
            )}
          </div>
        </div>
      )}

      </> /* end sent tab */
      )}

      <GenericManageRightsModal
        open={manageRightsOpen}
        onClose={() => setManageRightsOpen(false)}
        orgId={user?.orgId ?? null}
        entityKey="email_activity"
        subtitle="Per-user access for the Email activity list — stored locally per browser."
      />

      {pageToast ? (
        <div
          role="status"
          className={`fixed bottom-4 right-4 z-[200] max-w-sm rounded-lg border px-4 py-3 text-sm shadow-lg ${
            pageToast.kind === 'ok'
              ? 'border-emerald-500/40 bg-[var(--vscode-bg-elevated)] vscode-text-primary'
              : 'border-red-500/40 bg-[var(--vscode-bg-elevated)] text-red-400'
          }`}
        >
          {pageToast.text}
        </div>
      ) : null}
    </div>
  );
}
